import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export function Drawer({
  open,
  onClose,
  title,
  side = 'right',
  width = 'w-[440px]',
  height = 'h-[55vh]',
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  side?: 'right' | 'bottom';
  width?: string;
  height?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const panelPos =
    side === 'right'
      ? cn('top-0 right-0 h-full', width)
      : cn('bottom-0 left-0 right-0', height);

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-navy-deep/30 animate-fade-in" onClick={onClose} />
      <div
        className={cn(
          'absolute flex flex-col bg-white shadow-panel',
          side === 'right' ? 'animate-fade-in' : 'animate-slide-up rounded-t-xl',
          panelPos,
        )}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <div className="text-sm font-semibold text-ink">{title}</div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate hover:bg-cloud" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">{children}</div>
        {footer && <div className="border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
